import React, { useState, useEffect } from 'react';
import { Heart, Globe, Star, Award, Zap, Target } from 'lucide-react';
import { brandStory } from '../mock';
import SEO from '../components/SEO';

const BrandStoryPage = () => {
  const [currentCarouselImage, setCurrentCarouselImage] = useState(0);

  // Carousel images for the About Us page - Only package images
  const aboutCarouselImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_mitocandydev/artifacts/fiaizvnn_SwedishStyleCandy_Jo%CC%88nko%CC%88ping.png",
      alt: "Swedish Style Candy Jönköping"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mitocandydev/artifacts/u7ctfx60_SwedishStyleCandy_Karlek_Mockup.png",
      alt: "Swedish Style Candy Kärlek Mockup"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mitocandydev/artifacts/o90ewu6e_SwedishStyleCandy_Mockup.png",
      alt: "Swedish Style Candy Mockup"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mitocandydev/artifacts/haw9do3g_SwedishStyleCandy_Stockholm.png",
      alt: "Swedish Style Candy Stockholm"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_mitocandydev/artifacts/40thb0ci_Crystal_Candy_Bag__F_Revised-removebg-preview.png",
      alt: "Crystal Candy Bag"
    }
  ];

  // Auto-rotate carousel every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCarouselImage((prev) => (prev + 1) % aboutCarouselImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [aboutCarouselImages.length]);

  const values = [
    {
      icon: Heart,
      title: "PASSION FOR FLAVOR",
      description: "Every piece is crafted with love for the bold traditions of Swedish godis and global flavor culture."
    },
    {
      icon: Globe,
      title: "GLOBAL INSPIRATION",
      description: "We draw inspiration from Scandinavian simplicity and the curiosity of international taste profiles."
    },
    {
      icon: Star,
      title: "DESIGN CONSCIOUS",
      description: "Clean, minimal design meets bold flavors. Our confections are as beautiful as they are delicious."
    },
    {
      icon: Award,
      title: "PREMIUM QUALITY",
      description: "Using only the finest European ingredients to create memorable sensory experiences."
    }
  ];

  const timeline = [
    {
      year: "2020",
      title: "THE BEGINNING",
      description: "Founded with a vision to bring authentic Swedish-style candy to discerning businesses worldwide.",
      color: "bg-kato-cyan"
    },
    {
      year: "2021", 
      title: "RECIPE DEVELOPMENT",
      description: "Months of perfecting our unique flavor combinations and achieving the perfect Swedish candy texture.",
      color: "bg-kato-yellow"
    },
    {
      year: "2022",
      title: "FIRST PARTNERSHIPS",
      description: "Launched with select retail partners who shared our vision for premium confectionery experiences.",
      color: "bg-kato-navy"
    },
    {
      year: "2023",
      title: "CUSTOM SOLUTIONS",
      description: "Introduced custom candy creation services, allowing partners to develop their own unique offerings.",
      color: "bg-kato-cyan"
    },
    {
      year: "2024",
      title: "GLOBAL EXPANSION",
      description: "Scaled to serve 500+ partners across 45+ countries worldwide, making happy memories on 6 continents with consistent quality.",
      color: "bg-kato-yellow"
    },
    {
      year: "2025",
      title: "THE FUTURE",
      description: "Continuing to innovate while staying true to our core values of quality, design, and exceptional taste.",
      color: "bg-kato-navy"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="About KATO MITO | Global Swedish Candy Wholesale Making Happy Memories"
        description="Discover KATO MITO's passion for authentic Swedish candy worldwide. Premium bulk wholesale Swedish candy supplier with partners across 6 continents. Making happy memories globally with foamy candy & skull candy. Born from Scandinavian tradition, loved worldwide."
        keywords="Swedish candy brand, KATO MITO, global candy supplier, international Swedish candy, worldwide bulk candy, foamy candy international, skull candy worldwide, Swedish confections global"
        canonical={`${process.env.REACT_APP_BASE_URL || "https://katomito.com"}/story`}
      />
      
      {/* Born from Passion Section - Carousel Left, Text Right */}
      <section className="py-20 bg-blue-100/70 relative">
        {/* Blue translucent overlay */}
        <div className="absolute inset-0 bg-blue-200/30"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-kato-navy mb-8">
              BORN FROM <span className="text-kato-cyan">PASSION</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Carousel */}
            <div className="animate-slideInLeft">
              <div className="relative h-96 rounded-3xl overflow-hidden shadow-2xl">
                {/* Carousel Images */}
                {aboutCarouselImages.map((image, index) => (
                  <img
                    key={index}
                    src={image.src}
                    alt={image.alt}
                    className={`absolute inset-0 w-full h-full object-contain bg-gradient-to-br from-pink-100 via-purple-100 to-cyan-100 transition-all duration-1000 ${
                      index === currentCarouselImage 
                        ? 'opacity-100 transform translate-x-0' 
                        : 'opacity-0 transform translate-x-full'
                    }`}
                  />
                ))}
                
                {/* Carousel indicators */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {aboutCarouselImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentCarouselImage(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentCarouselImage 
                          ? 'bg-white scale-125 shadow-lg' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Right Side - Text Content */}
            <div className="animate-slideInRight">
              <div className="prose prose-xl">
                <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                  <strong className="text-kato-navy">KATO MITO</strong> is a Swedish-style candy brand born from a love of Scandinavian simplicity and indulgence. Inspired by the bold traditions of Swedish godis, the curiosity of global flavors, and the elegance of European ingredients, our candies transform confectionery into a true sensory experience — crafted to delight, spark memory, and create moments as memorable as the sweets themselves.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                  We blend unexpected flavor combinations—sweet, sour, salty—with clean, minimal design. Our confections are made for the flavor-forward, the design-conscious, and the quietly daring. These candies are more than just treats; they are a sensory experience designed to stand out on shelves and spark delight in every customer.
                </p>
                
                <p className="text-gray-700 leading-relaxed mb-8 text-lg">
                  Whether gifting or indulging, <strong className="text-kato-cyan">Kato Mito</strong> offers more than candy. It's a pause in your day, a little rebellion, a sophisticated sweet escape.
                </p>
                
                {/* Decorative elements */}
                <div className="flex items-center space-x-4 mt-8">
                  <div className="w-3 h-3 bg-kato-yellow rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-kato-cyan rounded-full animate-pulse delay-500"></div>
                  <div className="w-3 h-3 bg-kato-navy rounded-full animate-pulse delay-1000"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - Bold Cards */}
      <section className="py-20 bg-kato-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-kato-navy to-purple-900"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-5xl md:text-6xl font-black mb-6">
              OUR <span className="text-kato-yellow">VALUES</span>
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              What drives us to create exceptional Swedish-style candy experiences for businesses worldwide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div 
                  key={index}
                  className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 group text-center"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className="w-20 h-20 bg-kato-yellow rounded-3xl flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-10 h-10 text-kato-navy" />
                  </div>
                  <h3 className="text-2xl font-black mb-6 group-hover:text-kato-yellow transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section - Interactive */}
      <section className="py-20 bg-yellow-100/70 relative overflow-hidden">
        {/* Yellow translucent overlay */}
        <div className="absolute inset-0 bg-yellow-200/30"></div>
        
        {/* Candy icons background decorations */}
        <div className="absolute top-16 left-10 text-4xl opacity-20 animate-heartPop -z-10" style={{animationDelay: '0.5s'}}>🍬</div>
        <div className="absolute top-32 right-16 text-3xl opacity-25 animate-heartPop -z-10" style={{animationDelay: '1s'}}>🧸</div>
        <div className="absolute bottom-20 left-16 text-5xl opacity-15 animate-heartPop -z-10" style={{animationDelay: '1.5s'}}>🍭</div>
        <div className="absolute bottom-32 right-12 text-3xl opacity-22 animate-heartPop -z-10" style={{animationDelay: '2s'}}>🌟</div>
        <div className="absolute top-20 left-1/3 text-3xl opacity-18 animate-heartPop -z-10" style={{animationDelay: '0.8s'}}>🍩</div>
        <div className="absolute top-48 left-20 text-4xl opacity-20 animate-heartPop -z-10" style={{animationDelay: '1.2s'}}>🧁</div>
        <div className="absolute bottom-40 right-1/4 text-3xl opacity-24 animate-heartPop -z-10" style={{animationDelay: '1.8s'}}>🎂</div>
        <div className="absolute top-36 right-1/3 text-4xl opacity-16 animate-heartPop -z-10" style={{animationDelay: '2.2s'}}>🍪</div>
        <div className="absolute bottom-16 left-1/2 text-3xl opacity-28 animate-heartPop -z-10" style={{animationDelay: '0.3s'}}>🧊</div>
        <div className="absolute top-12 right-8 text-5xl opacity-12 animate-heartPop -z-10" style={{animationDelay: '2.5s'}}>🍰</div>
        <div className="absolute bottom-8 left-8 text-3xl opacity-26 animate-heartPop -z-10" style={{animationDelay: '1.7s'}}>🎀</div>
        <div className="absolute top-28 left-2/3 text-4xl opacity-14 animate-heartPop -z-10" style={{animationDelay: '0.9s'}}>🌺</div>
        <div className="absolute top-60 right-20 text-3xl opacity-20 animate-heartPop -z-10" style={{animationDelay: '1.4s'}}>🍫</div>
        <div className="absolute bottom-28 left-1/4 text-4xl opacity-18 animate-heartPop -z-10" style={{animationDelay: '2.3s'}}>🎈</div>
        <div className="absolute top-44 left-1/4 text-3xl opacity-22 animate-heartPop -z-10" style={{animationDelay: '1.6s'}}>🌈</div>
        <div className="absolute bottom-12 right-1/3 text-5xl opacity-16 animate-heartPop -z-10" style={{animationDelay: '0.7s'}}>🍒</div>
        
        {/* Additional candy icons in the middle area */}
        <div className="absolute top-1/2 left-1/4 text-4xl opacity-20 animate-heartPop -z-10" style={{animationDelay: '0.6s'}}>🍮</div>
        <div className="absolute top-1/2 right-1/4 text-3xl opacity-24 animate-heartPop -z-10" style={{animationDelay: '1.1s'}}>🥤</div>
        <div className="absolute top-1/3 left-1/2 text-5xl opacity-18 animate-heartPop -z-10" style={{animationDelay: '1.9s'}}>🍡</div>
        <div className="absolute top-2/3 left-1/2 text-3xl opacity-26 animate-heartPop -z-10" style={{animationDelay: '0.4s'}}>🥧</div>
        <div className="absolute top-40 left-2/5 text-4xl opacity-16 animate-heartPop -z-10" style={{animationDelay: '2.1s'}}>🍨</div>
        <div className="absolute bottom-36 right-2/5 text-3xl opacity-22 animate-heartPop -z-10" style={{animationDelay: '1.3s'}}>🧊</div>
        <div className="absolute top-52 right-1/2 text-4xl opacity-14 animate-heartPop -z-10" style={{animationDelay: '2.4s'}}>🍯</div>
        <div className="absolute bottom-44 left-2/5 text-3xl opacity-20 animate-heartPop -z-10" style={{animationDelay: '0.9s'}}>🍊</div>
        <div className="absolute top-1/3 right-1/3 text-5xl opacity-18 animate-heartPop -z-10" style={{animationDelay: '1.7s'}}>🍋</div>
        <div className="absolute bottom-1/3 left-1/3 text-3xl opacity-24 animate-heartPop -z-10" style={{animationDelay: '0.2s'}}>🍓</div>
        <div className="absolute top-2/3 right-2/5 text-4xl opacity-16 animate-heartPop -z-10" style={{animationDelay: '2.6s'}}>🥝</div>
        <div className="absolute bottom-2/3 left-2/5 text-3xl opacity-22 animate-heartPop -z-10" style={{animationDelay: '1.5s'}}>🍉</div>
        
        {/* Vertical connecting line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-kato-cyan opacity-30 transform -translate-x-1/2"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-5xl md:text-6xl font-black text-kato-navy mb-6">
              OUR <span className="text-kato-cyan">JOURNEY</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From humble beginnings to becoming a trusted name in premium Swedish-style confections.
            </p>
          </div>

          <div className="space-y-12">
            {timeline.map((item, index) => (
              <div 
                key={index}
                className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} animate-fadeInUp group`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                  <div className="bg-white p-8 rounded-3xl shadow-xl border-4 border-transparent group-hover:border-kato-cyan transition-all duration-300 hover:scale-105">
                    <div className={`text-4xl font-black mb-4 ${item.color === 'bg-kato-cyan' ? 'text-kato-cyan' : item.color === 'bg-kato-yellow' ? 'text-kato-yellow' : 'text-kato-navy'}`}>
                      {item.year}
                    </div>
                    <h3 className="text-2xl font-black text-kato-navy mb-4 group-hover:text-kato-cyan transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
                
                {/* Timeline Dot */}
                <div className={`w-8 h-8 ${item.color} rounded-full border-4 border-white shadow-lg z-10 group-hover:scale-125 transition-transform`}></div>
                
                <div className="w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blue border line between sections */}
      <div className="h-1 bg-kato-cyan"></div>

      {/* Quote Section - Powerful */}
      <section className="py-20 bg-gradient-to-r from-kato-yellow to-kato-cyan text-kato-navy relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23FFFFFF%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%227%22 cy=%227%22 r=%223%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="animate-fadeInUp">
            <blockquote className="text-3xl md:text-5xl font-black italic mb-12 max-w-5xl mx-auto leading-tight">
              "MORE THAN CANDY. A PAUSE IN YOUR DAY, A LITTLE REBELLION, A SOPHISTICATED SWEET ESCAPE."
            </blockquote>
            <div className="text-2xl font-black">
              — THE KATO MITO TEAM
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandStoryPage;